import { apiClient, queryClient } from '@tasama/api-client-query';
import { useAuthStore } from '../stores/auth-store';
import { env } from './env';
import { computeUploadTimeout } from '@tasama/utils';
import { i18n } from '@tasama/i18n';

/**
 * API Configuration for Portal App
 *
 * Auth is cookie-based (httpOnly). The BFF sets access_token and refresh_token
 * as httpOnly cookies on login. The browser sends them automatically on every
 * request because withCredentials is enabled.
 *
 * The BFF refreshes expired access tokens proactively, before a request
 * reaches its auth guard — FE never sees a 401 for the common expired-token
 * case. A 401 reaching here means refresh already failed server-side (a
 * genuine session expiry); the interceptor in httpClient.ts calls
 * onUnauthorized, which triggers handleUnauthorized() to clear app state and
 * redirect to login.
 */

const API_BASE_URL = env.API_BASE_URL;

// Send Accept-Language on every request based on the active locale
apiClient.setLocaleGetter(() => i18n.language);

// Configure base URL and default timeout
apiClient.updateConfig({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Send cookies with every request (required for httpOnly cookie auth)
apiClient.axios.defaults.withCredentials = true;

// Wire up the 401 handler — the BFF has already exhausted its own refresh
// attempt by the time FE sees a 401, so this always means a genuine session
// expiry.
apiClient.configureAuth({
  onUnauthorized: () => {
    useAuthStore.getState().handleUnauthorized();
  },
});

// Extend timeout for CMS-enriched routes — BFF calls Drupal (cms.timeout=30s)
// then enriches with backend data before responding. Only cold cache misses
// (post-restart or cache flush) hit Drupal; SWR cache handles warm/stale hits.
// 35s > BFF cms.timeout (30s) ensures BFF error reaches FE instead of FE cancel.
apiClient.axios.interceptors.request.use((config) => {
  const CMS_ENRICHED_PREFIXES = [
    '/cms',
    '/home',
    '/service-catalogue',
    '/services/business-landing',
    '/support-ticketing',
  ];
  if (CMS_ENRICHED_PREFIXES.some((p) => config.url?.startsWith(p))) {
    config.timeout = 35_000;
  }
  return config;
});

// Extend timeout for Business Advisor routes — LLM cold start can take 15–60s.
// Covers both /business-advisor/* (unauthenticated) and /business-advisor-user/*
// (authenticated) — getBusinessAdvisorUrl() in @tasama/business-advisor selects
// the path based on auth state.
apiClient.axios.interceptors.request.use((config) => {
  if (config.url?.includes('/business-advisor')) {
    config.timeout = 90_000;
  }
  return config;
});

// Extend timeout for multipart/file upload requests. Derives both total bytes
// and file count from FormData so the timeout covers the full BFF processing
// chain: upload to file service (Leg 1) + downstream API call (Leg 2).
apiClient.axios.interceptors.request.use((config) => {
  if (config.data instanceof FormData) {
    let totalBytes = 0;
    let fileCount = 0;
    config.data.forEach((value: FormDataEntryValue) => {
      if (value instanceof Blob) {
        totalBytes += value.size;
        fileCount++;
      }
    });
    if (totalBytes > 0) {
      config.timeout = computeUploadTimeout(totalBytes, fileCount);
    }
  }
  return config;
});

export { apiClient, queryClient };
